import React from 'react';
import { useTranslation } from 'react-i18next';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SubjectRoundedIcon from '@mui/icons-material/SubjectRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import AnimationRoundedIcon from '@mui/icons-material/AnimationRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StudentBreadcrumb from './StudentBreadcrumb';
import './StudentClassContent.css';

const materialIcons = {
  video: PlayCircleFilledRoundedIcon,
  audio: AudiotrackOutlinedIcon,
  image: ImageOutlinedIcon,
  link: LinkRoundedIcon,
  swf: AnimationRoundedIcon,
};
const materialLabels = { file: 'PDF', video: 'Video', audio: 'Audio', image: 'Image', link: 'Web Link', swf: 'Flash animation' };

// Presentation only: the page supplies its existing data and action handlers.
export default function StudentClassContent({
  role, groupName, groupImage, categories, openCategories, allCategoriesOpen,
  upcomingMeeting, studentUnreadChats, filterFilesByType, formatStartsIn,
  onToggleCategory, onToggleAll, onOpenFile, onMeetings, onChat, onQuiz,
  onExercises, onClasses, onJoinMeeting,
}) {
  const { t, i18n } = useTranslation();
  const actions = [
    { label: t('Upcoming Meetings'), icon: EventAvailableOutlinedIcon, tone: 'blue', onClick: onMeetings },
    { label: t('Messages'), icon: SmsOutlinedIcon, tone: 'orange', onClick: onChat, count: studentUnreadChats },
    ...(!role.isCoach ? [{ label: t('Quizzes'), icon: AssignmentTurnedInOutlinedIcon, tone: 'green', onClick: onQuiz }] : []),
    { label: t('Darssy AI Exercises'), icon: AutoAwesomeOutlinedIcon, tone: 'purple', onClick: onExercises },
  ];

  return (
    <div className="student-class-content" dir={i18n.dir()}>
      <StudentBreadcrumb
        items={[
          { label: role.isCoach ? t('My Programs') : t('My Classes'), onClick: onClasses },
          { label: groupName || role.classSingular },
        ]}
      />

      <section className="class-hero" aria-labelledby="class-title">
        <img className="class-hero-image" src={groupImage} alt={groupName || role.classSingular} />
        <div className="class-hero-copy">
          <h1 id="class-title" dir="auto">{groupName || role.classSingular}</h1>
          <p>{role.isCoach
            ? t('Access lessons, meetings and support for this program.')
            : t('Access lessons, meetings, quizzes and support for this class.')}</p>
          <div className="class-hero-stats">
            {upcomingMeeting && (
              <button className="class-stat" onClick={onMeetings}>
                <CalendarMonthOutlinedIcon />
                <span>{t('Next meeting')}: <b dir="ltr">{upcomingMeeting.date} · {upcomingMeeting.startTime}</b></span>
              </button>
            )}
            <div className="class-stat">
              <SubjectRoundedIcon />
              <span>{t('{{count}} Programme Sections', { count: categories.length })}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="class-actions" style={{ '--action-count': actions.length }}>
        {actions.map(({ label, icon: Icon, tone, onClick, count }) => (
          <button className="class-action" key={tone} onClick={onClick}>
            <span className={`class-action-icon class-tone-${tone}`}>
              <Icon />
              {count > 0 && <span className="class-message-badge">{count > 99 ? '99+' : count}</span>}
            </span>
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className="class-columns">
        <section className="class-panel class-programme" aria-labelledby="class-programme-title">
          <div className="class-panel-heading">
            <h2 id="class-programme-title">{role.isCoach ? t('Program') : t('Class Programme')}</h2>
            <button className="class-text-button" onClick={onToggleAll}>
              {allCategoriesOpen ? t('Collapse all') : t('Expand all')}
            </button>
          </div>
          <div className="class-sections">
            {categories.length === 0 ? (
              <div className="class-empty-materials">{t('لم يتم تحميل أي ملفات حتى الآن')}</div>
            ) : categories.map(([category, files], index) => {
              const visibleFiles = filterFilesByType(files);
              const expanded = !!openCategories[category];
              return (
                <section className="class-section" key={category}>
                  <button
                    className="class-section-toggle"
                    aria-expanded={expanded}
                    aria-controls={`class-section-${index}`}
                    onClick={() => onToggleCategory(category)}
                  >
                    <ExpandMoreRoundedIcon className={expanded ? 'class-chevron is-open' : 'class-chevron'} />
                    <span className="class-section-name" dir="auto">{category}</span>
                    <span className="class-item-count">{t('classMaterialCount', { count: visibleFiles.length, defaultValue: '{{count}} items' })}</span>
                  </button>
                  <div id={`class-section-${index}`} hidden={!expanded}>
                    <div className="class-materials">
                      {visibleFiles.map((file) => {
                        const Icon = materialIcons[file.type] || DescriptionOutlinedIcon;
                        const name = file.originalName || file.name;
                        const extension = (name || '').match(/\.([a-z0-9]+)$/i)?.[1]?.toUpperCase();
                        const isSlides = file.type === 'file' && ['PPT', 'PPTX', 'ODP'].includes(extension);
                        const typeLabel = isSlides ? `${t('Slides')} (${extension})` : t(materialLabels[file.type] || materialLabels.file);
                        return (
                          <div className={`class-material class-material-${isSlides ? 'slides' : file.type}`} key={file.id}>
                            <Icon className="class-material-icon" />
                            <button className="class-material-name" dir="auto" title={name} onClick={() => onOpenFile(file)}>{name}</button>
                            <span className="class-material-type">{typeLabel}</span>
                            <button className="class-open-file" onClick={() => onOpenFile(file)} aria-label={`${t('Open')}: ${name}`}>
                              <span>{t('Open')}</span><OpenInNewRoundedIcon />
                            </button>
                            <span className={`class-material-star${file.markedImportant ? ' is-important' : ''}`} title={file.markedImportant ? t('Important') : undefined}>
                              {file.markedImportant ? <StarRoundedIcon aria-label={t('Important')} /> : <StarBorderRoundedIcon />}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        </section>

        <aside className="class-side-panels">
          <section className="class-panel class-inbox" aria-labelledby="class-inbox-title">
            <div className="class-panel-heading"><h2 id="class-inbox-title">{t('Unread Messages')}</h2></div>
            <div className="class-inbox-body" aria-live="polite">
              <h3>{studentUnreadChats > 0 ? t('You have unread messages') : t('Empty inbox')}</h3>
              <p>{studentUnreadChats > 0
                ? t('classUnreadCount', { count: studentUnreadChats, defaultValue: '{{count}} unread messages' })
                : t('You have no unread messages right now.')}</p>
              <button className="class-text-button class-inbox-link" onClick={onChat}>{t('Open inbox')}<OpenInNewRoundedIcon /></button>
            </div>
          </section>

          {upcomingMeeting && (
            <section className="class-panel class-next-meeting" aria-labelledby="class-meeting-title">
              <div className="class-panel-heading">
                <h2 id="class-meeting-title">{t('Next meeting')}</h2>
                <button className="class-text-button" onClick={onMeetings}>{t('View all')}</button>
              </div>
              <div className="class-meeting-content">
                <div className="class-meeting-summary">
                  <span className="class-action-icon class-tone-blue"><CalendarMonthOutlinedIcon /></span>
                  <div><h3 dir="auto">{upcomingMeeting.title || t('حصة بدون عنوان')}</h3><p>{upcomingMeeting.date} · {upcomingMeeting.startTime}</p></div>
                </div>
                <div className="class-meeting-countdown">{t('تبدأ بعد:')} <b>{formatStartsIn(upcomingMeeting.startMs)}</b></div>
                <dl className="class-meeting-facts">
                  <div><dt>{t('وقت البداية')}</dt><dd>{upcomingMeeting.startTime}</dd></div>
                  <div><dt>{t('وقت النهاية')}</dt><dd>{upcomingMeeting.endTime || t('--:--')}</dd></div>
                  <div><dt>{t('المدة')}</dt><dd>{upcomingMeeting.duration ? t('{{count}} دقيقة', { count: upcomingMeeting.duration }) : t('—')}</dd></div>
                </dl>
                <p className="class-meeting-description" dir="auto">{upcomingMeeting.description || t('—')}</p>
                <button className="class-meeting-url class-text-button" dir="auto" onClick={onJoinMeeting} title={upcomingMeeting.url}>{upcomingMeeting.url || t('—')}</button>
                <button className="class-join-button" onClick={onJoinMeeting}>{t('انضم الآن')}<OpenInNewRoundedIcon /></button>
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
